/** This module contains utility functions for creating paths to the different routes managed by the portal. */
import { type LayerKey, NONE } from '@festina-life/api'
import { routeTag as r } from '@festina-life/components'
import type { ConfigurableServer, Optional } from '@festina-life/core'

import type { ConfigurationItem } from '../types/common'
import {
  NO_LAYER_PATH,
  READ_ONLY_WORKSPACE,
  SHARED_TENANT_PATH,
} from './constants'

type ServerRoutingParams = {
  server: Optional<string>
  workspaceName?: Optional<string>
  layer?: Optional<LayerKey>
  block: string
  branch: Optional<string>
}

export const server = ({
  server,
  layer,
  block,
  workspaceName,
  branch,
}: ServerRoutingParams) =>
  r`/${server || 'life'}/${branch || NONE}/${workspaceName || READ_ONLY_WORKSPACE}/${block}/${layer?.layerId || NO_LAYER_PATH}/${layer?.tenantId || SHARED_TENANT_PATH}`

type ConfigurationNavigationParams = {
  workspaceName: Optional<string>
  layer: LayerKey
  block: string
  server: ConfigurableServer
  branch: Optional<string>
  path?: Optional<string>
  untilChangesetKey?: Optional<string>
}

type TemplateNavigationParams = ConfigurationNavigationParams & {
  templateId: string
}

export const UNTIL_CHANGESET_KEY_QUERY_PARAM = 'untilChangesetKey'
export const untilChangesetQueryParam = (
  untilChangesetKey: Optional<string>,
) =>
  untilChangesetKey
    ? new URLSearchParams({
        [UNTIL_CHANGESET_KEY_QUERY_PARAM]: untilChangesetKey,
      }).toString()
    : ''

export const template = ({
  block,
  layer,
  server,
  templateId,
  workspaceName,
  branch,
  path,
  untilChangesetKey,
}: TemplateNavigationParams) => {
  const base = r`/${server}/${branch || NONE}/${
    workspaceName || READ_ONLY_WORKSPACE
  }/${block}/${layer.layerId || NO_LAYER_PATH}/${layer.tenantId || SHARED_TENANT_PATH}/template/${templateId}`

  return (
    base +
    (untilChangesetKey
      ? `?${untilChangesetQueryParam(untilChangesetKey)}`
      : '') +
    (path ? `#${path}` : '')
  )
}

type ParameterDefinitionNavigationParams = ConfigurationNavigationParams & {
  parameterDefinitionId: string
}

export const scratchpad = ({
  block,
  server,
  workspaceName,
  branch,
  layer,
}: ConfigurationNavigationParams) =>
  r`/${server}/${branch || NONE}/${
    workspaceName || READ_ONLY_WORKSPACE
  }/${block}/${layer.layerId || NO_LAYER_PATH}/${layer.tenantId || SHARED_TENANT_PATH}/scratchpad`

export const parameterDefinition = ({
  layer,
  block,
  server,
  parameterDefinitionId,
  workspaceName,
  branch,
  path,
  untilChangesetKey,
}: ParameterDefinitionNavigationParams) => {
  const base = r`/${server}/${branch || NONE}/${
    workspaceName || READ_ONLY_WORKSPACE
  }/${block}/${layer.layerId || NO_LAYER_PATH}/${layer.tenantId || SHARED_TENANT_PATH}/parameter/${parameterDefinitionId}`

  return (
    base +
    (untilChangesetKey
      ? `?${untilChangesetQueryParam(untilChangesetKey)}`
      : '') +
    (path ? `#${path}` : '')
  )
}

type EnumNavigationParams = ConfigurationNavigationParams & {
  enumId: string
  path: Optional<string>
}

const enumItem = ({
  layer,
  block,
  server,
  enumId,
  workspaceName,
  branch,
  path,
  untilChangesetKey,
}: EnumNavigationParams) =>
  r`/${server}/${branch || NONE}/${
    workspaceName || READ_ONLY_WORKSPACE
  }/${block}/${layer.layerId || NO_LAYER_PATH}/${layer.tenantId || SHARED_TENANT_PATH}/enum/${enumId}` +
  (untilChangesetKey ? `?${untilChangesetQueryParam(untilChangesetKey)}` : '') +
  (path ? `#${path}` : '')

type TenantNavigationParams = ConfigurationNavigationParams & {
  registeredTenantId: string
}

const tenantItem = ({
  layer,
  block,
  server,
  branch,
  workspaceName,
  registeredTenantId,
  untilChangesetKey,
}: TenantNavigationParams) =>
  r`/${server}/${branch || NONE}/${
    workspaceName || READ_ONLY_WORKSPACE
  }/${block}/${layer.layerId || NO_LAYER_PATH}/${layer.tenantId || SHARED_TENANT_PATH}/registeredTenant/${registeredTenantId}` +
  (untilChangesetKey ? `?${untilChangesetQueryParam(untilChangesetKey)}` : '')

type GlMapNavigationParams = ConfigurationNavigationParams & {
  mappingId: string
}

const glMapItem = ({
  layer,
  block,
  server,
  branch,
  workspaceName,
  mappingId,
  untilChangesetKey,
}: GlMapNavigationParams) =>
  r`/${server}/${branch || NONE}/${
    workspaceName || READ_ONLY_WORKSPACE
  }/${block}/${layer.layerId || NO_LAYER_PATH}/${layer.tenantId || SHARED_TENANT_PATH}/glMap/${mappingId}` +
  (untilChangesetKey ? `?${untilChangesetQueryParam(untilChangesetKey)}` : '')

type ReconciliationSpecNavigationParams = ConfigurationNavigationParams & {
  specId: string
}

const reconciliationSpecItem = ({
  layer,
  block,
  server,
  branch,
  workspaceName,
  specId,
  untilChangesetKey,
}: ReconciliationSpecNavigationParams) =>
  r`/${server}/${branch || NONE}/${
    workspaceName || READ_ONLY_WORKSPACE
  }/${block}/${layer.layerId || NO_LAYER_PATH}/${layer.tenantId || SHARED_TENANT_PATH}/reconciliationSpec/${specId}` +
  (untilChangesetKey ? `?${untilChangesetQueryParam(untilChangesetKey)}` : '')

type AnnulOperationNavigationParams = ConfigurationNavigationParams & {
  annulOperationKey: string
}

export const annulOperation = ({
  layer,
  block,
  server,
  branch,
  workspaceName,
  annulOperationKey,
  untilChangesetKey,
}: AnnulOperationNavigationParams) =>
  r`/${server}/${branch || NONE}/${
    workspaceName || READ_ONLY_WORKSPACE
  }/${block}/${layer.layerId || NO_LAYER_PATH}/${layer.tenantId || SHARED_TENANT_PATH}/annulOperation/${annulOperationKey}` +
  (untilChangesetKey ? `?${untilChangesetQueryParam(untilChangesetKey)}` : '')

export const configurationItem = ({
  server,
  layer,
  branch,
  itemId,
  workspaceName,
  type,
  path,
  untilChangesetKey,
  block,
}: {
  itemId: string
  type: ConfigurationItem
} & ConfigurationNavigationParams) => {
  switch (type) {
    case 'template':
    case 'hideTemplate':
    case 'library': {
      return template({
        workspaceName,
        server,
        templateId: itemId,
        layer,
        branch,
        path,
        untilChangesetKey,
        block,
      })
    }
    case 'parameterValue':
    case 'parameterValueAmendment':
    case 'parameterDefinition': {
      return parameterDefinition({
        workspaceName,
        server,
        parameterDefinitionId: itemId,
        layer,
        branch,
        path,
        untilChangesetKey,
        block,
      })
    }
    case 'enumLiteral':
    case 'enumDefinition': {
      return enumItem({
        workspaceName,
        server,
        enumId: itemId,
        layer,
        branch,
        untilChangesetKey,
        path,
        block,
      })
    }
    case 'tenant': {
      return tenantItem({
        workspaceName,
        server,
        registeredTenantId: itemId,
        layer,
        branch,
        untilChangesetKey,
        block,
      })
    }
    case 'glMap':
    case 'hideGlMap': {
      return glMapItem({
        workspaceName,
        server,
        mappingId: itemId,
        layer,
        branch,
        untilChangesetKey,
        block,
      })
    }
    case 'reconciliationSpec': {
      return reconciliationSpecItem({
        workspaceName,
        server,
        specId: itemId,
        layer,
        branch,
        untilChangesetKey,
        block,
      })
    }
    case 'annulOperation': {
      return annulOperation({
        workspaceName,
        server,
        annulOperationKey: itemId,
        layer,
        branch,
        untilChangesetKey,
        block,
      })
    }
  }
}

export const enumDefinitionPath = (enumName: string) => r`${enumName}`
export const enumLiteralPath = ({
  enumName,
  literalName,
}: {
  enumName: string
  literalName: string
}) => r`${enumName}.${literalName}`
